import { Button } from '@/components/ui';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type {
  DecisionSource,
  HumanReviewFacets,
  HumanReviewRequestStateFilter,
  HumanReviewSort,
} from '@/lib/admin/client';

import {
  DECISION_SOURCE_OPTIONS,
  facetCount,
  withCount,
} from './humanReviewFacetLabels';
import { humanizeSubjectType } from './subjectType';

const ALL = 'all';

// The primary axis: which queue you're looking at. Always-visible segmented
// control (single-select), not a dropdown, because it's flipped constantly.
const REVIEW_STATES: Array<{
  label: string;
  value: HumanReviewRequestStateFilter;
}> = [
  { label: 'Pending', value: 'pending' },
  { label: 'Needs changes', value: 'changes_requested' },
  { label: 'Approved', value: 'approved' },
  { label: 'Rejected', value: 'rejected' },
  { label: 'Expired', value: 'expired' },
  { label: 'All', value: 'all' },
];

const PRIORITIES = ['P0', 'P1', 'P2'];

// Oldest first leads: it is the default on the pending worklist (work the
// longest-waiting items before they hit SLA expiry).
const SORTS: Array<{ label: string; value: HumanReviewSort }> = [
  { label: 'Oldest first', value: 'oldest' },
  { label: 'Newest first', value: 'newest' },
];

// One code path for the secondary refinement dropdowns (Type / Priority /
// Decision). Each is single-select with an "all" reset; the sort control is a
// separate always-on select (no reset).
type FilterOption = { value: string; label: string };

function FilterSelect({
  value,
  onChange,
  allLabel,
  options,
  ariaLabel,
}: {
  value: string | null;
  onChange: (value: string | null) => void;
  allLabel: string;
  options: FilterOption[];
  ariaLabel: string;
}) {
  return (
    <Select
      value={value ?? ALL}
      onValueChange={(next) => onChange(next === ALL ? null : next)}
    >
      <SelectTrigger size="sm" aria-label={ariaLabel}>
        <SelectValue placeholder={allLabel} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={ALL}>{allLabel}</SelectItem>
        {options.map((option) => (
          <SelectItem key={option.value} value={option.value}>
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export type ReviewToolbarProps = {
  state: HumanReviewRequestStateFilter;
  onReviewStateChange: (value: HumanReviewRequestStateFilter) => void;
  subjectType: string | null;
  onItemTypeChange: (value: string | null) => void;
  priority: string | null;
  onPriorityChange: (value: string | null) => void;
  decisionSource: DecisionSource | null;
  onDecisionSourceChange: (value: DecisionSource | null) => void;
  sort: HumanReviewSort;
  onSortChange: (value: HumanReviewSort) => void;
  facets?: HumanReviewFacets;
};

export function HumanReviewToolbar({
  state,
  onReviewStateChange,
  subjectType,
  onItemTypeChange,
  priority,
  onPriorityChange,
  decisionSource,
  onDecisionSourceChange,
  sort,
  onSortChange,
  facets,
}: ReviewToolbarProps) {
  const itemTypeOptions: FilterOption[] = facets
    ? Object.keys(facets.subjectType)
        .sort()
        .map((key) => ({
          value: key,
          label: withCount(
            humanizeSubjectType(key),
            facetCount(facets.subjectType, key)
          ),
        }))
    : [];

  const priorityOptions: FilterOption[] = PRIORITIES.map((value) => ({
    value,
    label: withCount(value, facetCount(facets?.priority, value)),
  }));

  const decisionOptions: FilterOption[] = DECISION_SOURCE_OPTIONS.map(
    (source) => ({
      value: source.value,
      label: withCount(
        source.label,
        facetCount(facets?.decisionSource, source.value)
      ),
    })
  );

  return (
    <div className="flex flex-wrap items-center gap-2">
      {/* Primary axis: the queue you're viewing. */}
      {REVIEW_STATES.map((option) => (
        <Button
          key={option.value}
          type="button"
          variant={state === option.value ? 'selected' : 'outline'}
          aria-pressed={state === option.value}
          aria-label={`Filter ${option.label.toLowerCase()}`}
          onClick={() => onReviewStateChange(option.value)}
        >
          {option.value === 'all'
            ? option.label
            : withCount(option.label, facetCount(facets?.state, option.value))}
        </Button>
      ))}

      {/* Secondary refinements, then sort last on the far right. */}
      <div className="ml-auto flex flex-wrap items-center gap-2">
        {itemTypeOptions.length > 0 && (
          <FilterSelect
            value={subjectType}
            onChange={onItemTypeChange}
            allLabel="All types"
            options={itemTypeOptions}
            ariaLabel="Filter by item type"
          />
        )}
        <FilterSelect
          value={priority}
          onChange={onPriorityChange}
          allLabel="All priorities"
          options={priorityOptions}
          ariaLabel="Filter by priority"
        />
        <FilterSelect
          value={decisionSource}
          onChange={(value) =>
            onDecisionSourceChange(
              value === 'human' || value === 'policy' || value === 'system'
                ? value
                : null
            )
          }
          allLabel="Any decision"
          options={decisionOptions}
          ariaLabel="Filter by decision maker"
        />
        <Select
          value={sort}
          onValueChange={(value) =>
            onSortChange(value === 'oldest' ? 'oldest' : 'newest')
          }
        >
          <SelectTrigger size="sm" aria-label="Sort order">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {SORTS.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}
